/**
 * Live session utilities for Morph browser sessions
 * 
 * Provides helpers for embedding and sharing live browser sessions with WebRTC streaming.
 */

import type { LiveSessionOptions, IframeOptions } from './types.js';

/**
 * Preset configurations for common use cases
 */
export const LIVE_PRESETS = {
  /** Read-only monitoring (no interaction) */
  readonly: { interactive: false } as LiveSessionOptions,
  /** Interactive control (human-in-the-loop) */
  interactive: { interactive: true } as LiveSessionOptions,
  /** Watch-only without controls */
  monitoring: { interactive: false, showControls: false } as LiveSessionOptions,
} as const;

/**
 * Build a live session URL with query parameters
 * 
 * @param debugUrl - Live session debug URL (e.g., from task.debugUrl)
 * @param options - Live session configuration options
 * @returns URL with query parameters for iframe embedding
 * 
 * @example
 * ```typescript
 * const url = buildLiveUrl(task.debugUrl, { interactive: true });
 * // Returns: https://example.com/sessions/abc?interactive=true
 * ```
 */
export function buildLiveUrl(
  debugUrl: string,
  options: LiveSessionOptions = {}
): string {
  if (!debugUrl) {
    throw new Error(
      'debugUrl is required. Ensure your backend returns debugUrl in the task response. ' +
      'Contact support@morphllm.com if you need help.'
    );
  }

  const normalized = normalizeLiveUrl(debugUrl);
  const url = new URL(normalized);
  
  // Add query parameters for supported options
  if (options.interactive !== undefined) {
    url.searchParams.set('interactive', String(options.interactive));
  }
  
  if (options.theme) {
    url.searchParams.set('theme', options.theme);
  }
  
  if (options.showControls !== undefined) {
    url.searchParams.set('showControls', String(options.showControls));
  }
  
  if (options.pageId) {
    url.searchParams.set('pageId', options.pageId);
  }
  
  if (options.pageIndex) {
    url.searchParams.set('pageIndex', options.pageIndex);
  }
  
  return url.toString();
}

/**
 * Convert a CDP WebSocket scheme to HTTPS.
 *
 * Browser-use's live viewer expects `wss=https://UUID.cdpN.browser-use.com`
 * but CDP URLs use `wss://`. This swaps the scheme.
 */
function cdpToHttps(wsUrl: string): string {
  return wsUrl.replace(/^wss:\/\//, 'https://').replace(/^ws:\/\//, 'http://');
}

/**
 * Normalize any debug URL into a valid browser-use live viewer URL.
 *
 * Handles three input formats:
 *  1. Already-correct `https://live.browser-use.com?wss=https://...` — pass through
 *  2. Already-correct but with wrong scheme `...?wss=wss://...` — fix the wss param
 *  3. Raw CDP URL `wss://UUID.cdpN.browser-use.com` — wrap into live viewer URL
 */
function normalizeLiveUrl(debugUrl: string): string {
  const trimmed = debugUrl.trim();
  if (!trimmed) return trimmed;

  // Case 3: raw CDP WebSocket URL → wrap into live viewer
  if (trimmed.startsWith('wss://') || trimmed.startsWith('ws://')) {
    return `https://live.browser-use.com?wss=${encodeURIComponent(cdpToHttps(trimmed))}`;
  }

  let url: URL;
  try {
    url = new URL(trimmed);
  } catch {
    return trimmed;
  }

  // Case 2: live viewer URL with wrong scheme in wss param → fix it
  const wssParam = url.searchParams.get('wss');
  if (wssParam && (wssParam.startsWith('wss://') || wssParam.startsWith('ws://'))) {
    url.searchParams.set('wss', cdpToHttps(wssParam));
  }

  return url.toString();
}

/**
 * Build iframe HTML for embedding a live session
 * 
 * @param debugUrl - Live session debug URL
 * @param options - Iframe configuration including dimensions and session options
 * @returns HTML iframe element as string
 * 
 * @example
 * ```typescript
 * const iframe = buildLiveIframe(task.debugUrl, {
 *   interactive: true,
 *   width: '100%',
 *   height: '600px'
 * });
 * ```
 */
export function buildLiveIframe(
  debugUrl: string,
  options: IframeOptions = {}
): string {
  const {
    width = '100%',
    height = '600px',
    style = '',
    className = '',
    ...sessionOptions
  } = options;

  const src = buildLiveUrl(debugUrl, sessionOptions);
  
  // Convert numeric dimensions to pixels
  const widthStr = typeof width === 'number' ? `${width}px` : width;
  const heightStr = typeof height === 'number' ? `${height}px` : height;
  
  // Build style attribute
  const baseStyle = `width: ${widthStr}; height: ${heightStr}; border: none;`;
  const fullStyle = style ? `${baseStyle} ${style}` : baseStyle;
  
  // Build iframe attributes
  const attributes = [
    `src="${src}"`,
    `style="${fullStyle}"`,
  ];
  
  if (className) {
    attributes.push(`class="${className}"`);
  }
  
  return `<iframe ${attributes.join(' ')}></iframe>`;
}

/**
 * Build complete embed code with HTML snippet
 * 
 * @param debugUrl - Live session debug URL
 * @param options - Iframe configuration
 * @returns Multi-line HTML snippet ready to copy-paste
 * 
 * @example
 * ```typescript
 * const code = buildEmbedCode(task.debugUrl, { interactive: false });
 * console.log(code);
 * // <!-- Embed Morph Live Session -->
 * // <iframe src="..." style="..."></iframe>
 * ```
 */
export function buildEmbedCode(
  debugUrl: string,
  options: IframeOptions = {}
): string {
  const iframe = buildLiveIframe(debugUrl, options);
  return `<!-- Embed Morph Live Session -->\n${iframe}`;
}

/**
 * Get live session options from preset name or custom config
 * 
 * @internal
 */
export function resolvePreset(
  optionsOrPreset?: string | IframeOptions
): IframeOptions {
  if (!optionsOrPreset) {
    return {};
  }
  
  if (typeof optionsOrPreset === 'string') {
    const preset = LIVE_PRESETS[optionsOrPreset as keyof typeof LIVE_PRESETS];
    if (!preset) {
      throw new Error(
        `Unknown preset: ${optionsOrPreset}. Available presets: ${Object.keys(LIVE_PRESETS).join(', ')}`
      );
    }
    return preset;
  }
  
  return optionsOrPreset;
}
